import { create } from "zustand";
import i18n from "../i18n";

interface PeriodCurrency {
  currency_code: string;
  currency_symbol: string;
}

interface AppState {
  selectedYear: number;
  selectedClass: string | null;
  years: number[];
  yearsLoaded: boolean;
  currencyCode: string;
  currencySymbol: string;
  /** Per-year currency map — populated from financial periods API response */
  periodCurrencies: Record<number, PeriodCurrency>;
  languageCode: string;
  /** Currently selected platform functionality */
  selectedFunctionality: string;
  setSelectedYear: (year: number) => void;
  setSelectedClass: (businessClass: string | null) => void;
  setYears: (years: number[]) => void;
  setCurrency: (code: string, symbol: string) => void;
  /** Set per-year currency map from financial periods and update active currency */
  setPeriodCurrencies: (map: Record<number, PeriodCurrency>) => void;
  setLanguage: (code: string) => void;
  setSelectedFunctionality: (functionality: string) => void;
}

/** Fallback years shown only before the API response arrives (yearsLoaded=false). */
const FALLBACK_YEARS: number[] = [];

const STORED_FUNC_KEY = "selectedFunctionality";

export const useAppStore = create<AppState>((set, get) => ({
  selectedYear: 2025,
  selectedClass: null,
  years: FALLBACK_YEARS,
  yearsLoaded: false,
  currencyCode: "KES",
  currencySymbol: "KSh",
  periodCurrencies: {},
  languageCode: "en",
  selectedFunctionality:
    localStorage.getItem(STORED_FUNC_KEY) || "audit_workbook",

  setSelectedYear: (year: number) => {
    const pc = get().periodCurrencies[year];
    if (pc) {
      set({
        selectedYear: year,
        currencyCode: pc.currency_code,
        currencySymbol: pc.currency_symbol,
      });
    } else {
      set({ selectedYear: year });
    }
  },
  setSelectedClass: (businessClass: string | null) =>
    set({ selectedClass: businessClass }),
  setYears: (years: number[]) => {
    const calcYears = years.length > 0 ? years : FALLBACK_YEARS;
    set((state) => ({
      years: calcYears,
      yearsLoaded: true,
      // Keep selectedYear if it's still valid, otherwise use the latest
      selectedYear: calcYears.includes(state.selectedYear)
        ? state.selectedYear
        : calcYears[calcYears.length - 1],
    }));
  },
  setCurrency: (code: string, symbol: string) =>
    set({ currencyCode: code, currencySymbol: symbol }),
  setPeriodCurrencies: (map: Record<number, PeriodCurrency>) => {
    const state = get();
    const pc = map[state.selectedYear];
    if (pc) {
      set({
        periodCurrencies: map,
        currencyCode: pc.currency_code,
        currencySymbol: pc.currency_symbol,
      });
    } else {
      set({ periodCurrencies: map });
    }
  },
  setLanguage: (code: string) => {
    i18n.changeLanguage(code);
    set({ languageCode: code });
  },
  setSelectedFunctionality: (functionality: string) => {
    localStorage.setItem(STORED_FUNC_KEY, functionality);
    set({ selectedFunctionality: functionality });
  },
}));
